
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiClient } from './apiClient';
import { useAuth } from './firebase';

const MEMBER_LIMIT = 5;

const Modal = ({ children, onClose }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50" onClick={onClose}>
        <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            {children}
        </div>
    </div>
);

const DeleteModal = ({ member, onDelete, onClose, t }) => {
    if (!member) return null;
    return (
        <Modal onClose={onClose}>
            <h2 className="text-xl font-bold mb-4">{t('deleteModalTitle')}</h2>
            <p className="mb-6">{t('deleteConfirmationText')}</p>
            <div className="flex justify-end gap-2">
                <button onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">{t('cancelButton')}</button>
                <button onClick={onDelete} className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700">{t('confirmDeleteButton')}</button>
            </div>
        </Modal>
    );
};

const TeamManagement = () => {
    const { t } = useTranslation('teamManagement');
    const navigate = useNavigate();
    
    const [teamMembers, setTeamMembers] = useState([]);
    const [newMemberEmail, setNewMemberEmail] = useState('');
    const [isAdmin, setIsAdmin] = useState(false);
    const [loading, setLoading] = useState(true);
    const [isInviting, setIsInviting] = useState(false);
    const [memberLimitReached, setMemberLimitReached] = useState(false);

    const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
    const [deletingMember, setDeletingMember] = useState(null);

    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    
    const currentUser = useAuth();

    const fetchTeamData = useCallback(async () => {
        if (currentUser) {
            setLoading(true);
            try {
                const token = await currentUser.getIdToken();
                const response = await apiClient.get('/v1/team', { headers: { Authorization: `Bearer ${token}` } });
                
                if (!response.ok) {
                    throw new Error(`API call failed with status: ${response.status}`);
                }

                const data = await response.json();

                setTeamMembers(data.members || []);
                setIsAdmin(data.isAdmin || false);
                setError('');
            } catch (err) {
                console.error("Error fetching team data:", err);
                setError(t('fetchError'));
            } finally {
                setLoading(false);
            }
        }
    }, [currentUser, t]);

    useEffect(() => {
        if (currentUser) {
            fetchTeamData();
        }
    }, [fetchTeamData, currentUser]);

    useEffect(() => {
        setMemberLimitReached(teamMembers.length >= MEMBER_LIMIT);
    }, [teamMembers]);

    const handleApiError = async (err, defaultError) => {
        if (err && typeof err.json === 'function') {
            try {
                const errorJson = await err.json();
                return errorJson.error || defaultError;
            } catch (e) { /* fallback */ }
        }
        if (err.response && typeof err.response.json === 'function') {
            try {
                const errorJson = await err.response.json();
                return errorJson.error || defaultError;
            } catch (e) { /* fallback */ }
        }
        return err.message || defaultError;
    };

    const handleAddMember = async (e) => {
        e.preventDefault();
        if (!newMemberEmail) {
            setError(t('emailRequired'));
            return;
        }
        if (memberLimitReached) {
            setError(t('memberLimitReached'));
            return;
        }
        setIsInviting(true);
        setError('');
        setSuccess('');
        try {
            const token = await currentUser.getIdToken();
            const response = await apiClient.post('/v1/team/invite', { email: newMemberEmail }, { headers: { Authorization: `Bearer ${token}` } });
            if (!response.ok) throw response;

            setSuccess(t('inviteSuccess'));
            setNewMemberEmail('');
            fetchTeamData();
        } catch (err) {
            const errorMessage = await handleApiError(err, t('inviteError'));
            setError(errorMessage);
        } finally {
            setIsInviting(false);
        }
    };

    const handleDeleteMember = async () => {
        if (!deletingMember) return;

        if (deletingMember.role === 'admin') {
            const adminCount = teamMembers.filter(m => m.role === 'admin').length;
            if (adminCount <= 1) {
                setError(t('lastAdminDeleteError'));
                setDeleteModalOpen(false);
                return;
            }
        }

        setError('');
        setSuccess('');
        try {
            const token = await currentUser.getIdToken();
            const response = await apiClient.delete(`/v1/team/members/${deletingMember.id}`, { headers: { Authorization: `Bearer ${token}` } });
            if (!response.ok) throw response;

            setSuccess(t('deleteSuccess'));
            setDeleteModalOpen(false);
            fetchTeamData();
        } catch (err) {
            const errorMessage = await handleApiError(err, t('deleteError'));
            setError(errorMessage);
            setDeleteModalOpen(false);
        }
    };
    
    const openDeleteModal = (member) => {
        setDeletingMember(member);
        setDeleteModalOpen(true);
    };

    const openPermissionsPage = (member) => {
        navigate(`/settings/team-management/${member.id}/permissions`, { state: { member } });
    };

    if (loading) {
        return <div className="p-6">{t('loading')}</div>;
    }

    if (!isAdmin) {
        return <div className="p-6"><h1 className="text-2xl font-bold mb-4">{t('mainTitle')}</h1><p>{t('notAdmin')}</p></div>;
    }

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            <h1 className="text-3xl font-bold mb-6 text-gray-800">{t('mainTitle')}</h1>

            {error && <p className="text-red-700 mb-4 p-3 bg-red-100 border border-red-200 rounded-md">{error}</p>}
            {success && <p className="text-green-700 mb-4 p-3 bg-green-100 border border-green-200 rounded-md">{success}</p>}

            <div className="mb-8 bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-2xl font-semibold mb-4 text-gray-700">{t('addMemberTitle')}</h2>
                {memberLimitReached ? (
                    <p className="text-yellow-700 p-3 bg-yellow-100 border border-yellow-200 rounded-md">{t('memberLimitReached')}</p>
                ) : (
                    <form onSubmit={handleAddMember} className="flex items-center space-x-4">
                        <input
                            type="email"
                            value={newMemberEmail}
                            onChange={(e) => setNewMemberEmail(e.target.value)}
                            placeholder={t('emailPlaceholder')}
                            className="flex-grow p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
                            required
                        />
                        <button
                            type="submit"
                            className="bg-indigo-600 text-white font-semibold py-3 px-6 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-indigo-300"
                            disabled={isInviting}
                        >
                            {isInviting ? t('inviting') : t('addMemberButton')}
                        </button>
                    </form>
                )}
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-2xl font-semibold mb-4 text-gray-700">{t('teamMembersTitle')}</h2>
                <div className="overflow-x-auto">
                    <table className="min-w-full bg-white">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="py-3 px-4 text-left font-semibold text-gray-600">{t('nameHeader')}</th>
                                <th className="py-3 px-4 text-left font-semibold text-gray-600">{t('emailHeader')}</th>
                                <th className="py-3 px-4 text-left font-semibold text-gray-600">{t('roleHeader')}</th>
                                <th className="py-3 px-4 text-left font-semibold text-gray-600">{t('actionsHeader')}</th>
                            </tr>
                        </thead>
                        <tbody className="text-gray-700">
                            {teamMembers.length > 0 ? teamMembers.map((member) => (
                                <tr key={member.id} className="border-b border-gray-200 hover:bg-gray-50">
                                    <td className="py-3 px-4">{member.representativeName || '-'}</td>
                                    <td className="py-3 px-4 font-mono">{member.email}</td>
                                    <td className="py-3 px-4"><span className={`px-2 py-1 text-xs font-semibold rounded-full ${member.role === 'admin' ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-800'}`}>{t(member.role === 'admin' ? 'roleAdmin' : 'roleMember')}</span></td>
                                    <td className="py-3 px-4 whitespace-nowrap space-x-2">
                                        <button onClick={() => openPermissionsPage(member)} className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">{t('editButton')}</button>
                                        <button onClick={() => openDeleteModal(member)} className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500" disabled={currentUser.uid === member.id}>{t('deleteButton')}</button>
                                    </td>
                                </tr>
                            )) : (
                                <tr><td colSpan="4" className="text-center py-6 text-gray-500">{t('noMembers')}</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {isDeleteModalOpen && (
                <DeleteModal
                    member={deletingMember}
                    onDelete={handleDeleteMember}
                    onClose={() => setDeleteModalOpen(false)}
                    t={t}
                />
            )}
        </div>
    );
};

export default TeamManagement;
